<template>
  <div
    class="fm-item"
    :class="{
      'is-selected': selected,
      'is-grid': view === 'grid',
      'is-list': view === 'list',
      'is-folder': item.type === 'folder',
      'is-cut': item._cut,
      'is-dragging': dragging,
    }"
    :style="view === 'grid' ? { '--type-color': iconInfo.color } : {}"
    :draggable="true"
    @click.exact="$emit('select', item)"
    @click.ctrl.exact="$emit('multi-select', item)"
    @click.meta.exact="$emit('multi-select', item)"
    @click.shift.exact="$emit('range-select', item)"
    @dblclick="$emit('open', item)"
    @contextmenu.prevent="$emit('context-menu', { item, event: $event })"
    @dragstart="onDragStart"
    @dragend="dragging = false"
    @dragover.prevent
    @drop.prevent="item.type === 'folder' && $emit('drop', { target: item, event: $event })"
  >
    <!-- Icon area (shared grid + list) -->
    <div class="fm-icon-area">
      <FileIcon :item="item" :showThumbnail="view === 'grid'" />
      <FileChips v-if="item.chips?.length" :chips="item.chips" />
      <div v-if="view === 'grid'" class="fm-type-strip"></div>
    </div>

    <!-- Grid: name + size below icon -->
    <template v-if="view === 'grid'">
      <div v-if="!renaming" class="fm-name">{{ item.name }}</div>
      <input v-else ref="renameEl" v-model="renameValue" class="fm-rename-input"
        @blur="commitRename" @keydown.enter="commitRename" @keydown.esc="cancelRename" @click.stop />
      <span class="fm-meta">{{ sizeOrCount }}</span>
    </template>

    <!-- List: name + size + date + type columns -->
    <template v-if="view === 'list'">
      <div v-if="!renaming" class="fm-name" :title="item.name">{{ item.name }}</div>
      <input v-else ref="renameEl" v-model="renameValue" class="fm-rename-input"
        @blur="commitRename" @keydown.enter="commitRename" @keydown.esc="cancelRename" @click.stop />
      <span class="fm-list-size">{{ sizeOrCount }}</span>
      <span class="fm-list-date">{{ formatDate(item.modifiedAt) }}</span>
      <span class="fm-list-type">{{ extLabel }}</span>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import FileIcon from './FileIcon.vue';
import FileChips from './FileChips.vue';
import { useFileIcons } from './useFileIcons.js';

const props = defineProps({
  item: { type: Object, required: true },
  selected: Boolean,
  view: { type: String, default: 'grid' },
});

const emit = defineEmits(['select','multi-select','range-select','open','context-menu','rename','drop']);

const { getIconInfo } = useFileIcons();
const iconInfo = computed(() => getIconInfo(props.item));

const dragging = ref(false);
const renaming = ref(false);
const renameValue = ref('');
const renameEl = ref(null);

const extLabel = computed(() => {
  if (props.item.type === 'folder') return 'Folder';
  return (props.item.extension || props.item.name?.split('.').pop() || '').toUpperCase() || 'File';
});

const sizeOrCount = computed(() => {
  if (props.item.type === 'folder') {
    return props.item.childCount != null ? props.item.childCount + ' items' : '';
  }
  return formatSize(props.item.size);
});

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(2) + ' GB';
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function onDragStart(e) {
  dragging.value = true;
  e.dataTransfer.setData('text/plain', props.item.id);
}

function startRename() {
  renaming.value = true;
  renameValue.value = props.item.name;
  nextTick(() => { renameEl.value?.focus(); renameEl.value?.select(); });
}

function commitRename() {
  if (!renaming.value) return;
  renaming.value = false;
  const val = renameValue.value.trim();
  if (val && val !== props.item.name) emit('rename', { item: props.item, newName: val });
}

function cancelRename() { renaming.value = false; }

defineExpose({ startRename });
</script>
<style scoped>
.fm-item {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  border: 1.5px solid transparent;
  transition: background .1s, border-color .1s, opacity .12s, box-shadow .1s;
  user-select: none;
  outline: none;
}
.fm-item:hover { background: var(--fm-item-hover); }
.fm-item.is-selected {
  border-color: var(--fm-accent);
  background: var(--fm-item-selected);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fm-accent) 15%, transparent);
}
.fm-item.is-cut { opacity: .4; }
.fm-item.is-dragging { opacity: .25; }

/* Grid card */
.fm-item.is-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 4px 8px;
  width: 100%;
  gap: 0;
}
.fm-item.is-grid .fm-icon-area {
  position: relative;
  width: 76px; height: 76px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background: color-mix(in srgb, var(--type-color, var(--fm-accent)) 10%, var(--fm-surface));
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
  border: 1px solid color-mix(in srgb, var(--type-color, var(--fm-accent)) 20%, transparent);
}
.fm-item.is-grid .fm-type-strip {
  position: absolute; bottom: 0; left: 0; right: 0;
  height: 3px;
  background: var(--type-color, var(--fm-accent));
}
.fm-item.is-grid .fm-name {
  font-size: 0.72rem; color: var(--fm-text);
  text-align: center; word-break: break-all;
  display: -webkit-box; -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; overflow: hidden; width: 100%; line-height: 1.35;
}
.fm-item.is-grid .fm-meta { font-size: 0.62rem; color: var(--fm-text-muted); margin-top: 2px; }

/* List row */
.fm-item.is-list {
  display: grid;
  grid-template-columns: 32px 1fr 80px 110px 56px;
  align-items: center;
  padding: 4px 10px; gap: 10px; border-radius: 5px;
}
.fm-item.is-list .fm-icon-area {
  position: relative; width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
}
/* Shrink icon glyph in list rows */
.fm-item.is-list .fm-icon-area :deep(.fm-icon-glyph) { font-size: 1.35rem; }
.fm-item.is-list .fm-type-strip { display: none; }
.fm-item.is-list .fm-name {
  font-size: 0.8rem; color: var(--fm-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fm-item.is-list .fm-meta { display: none; }
.fm-item.is-list .fm-list-size,
.fm-item.is-list .fm-list-date,
.fm-item.is-list .fm-list-type { font-size: 0.71rem; color: var(--fm-text-muted); white-space: nowrap; }

.fm-rename-input {
  width: 100%; font-size: 0.75rem; padding: 2px 5px;
  border: 1px solid var(--fm-accent); border-radius: 4px;
  background: var(--fm-surface); color: var(--fm-text); outline: none; text-align: center;
}
.fm-item.is-list .fm-rename-input { text-align: left; }
</style>